import { LETTER_POINTS, SCORING } from './constants.ts'
import type { Edition } from './edition.ts'

export interface WordScore {
  /** Sum of the letters' points. */
  readonly pts: number
  /** 1 plus the tier of every gold letter: each time it appears (rules v1) or once per word (v2). */
  readonly mult: number
  /** pts × mult */
  readonly total: number
}

/** What scoring needs from an edition: its gold letters and the rules version it was published under. */
export type Scoring = Pick<Edition, 'gold' | 'rulesVersion'>

function scoringFor(rulesVersion: number) {
  const scoring = SCORING[rulesVersion]
  if (!scoring) throw new RangeError(`Unknown rules version ${rulesVersion}`)
  return scoring
}

/** A letter's points under a rules version: Scrabble's, capped at 5 from v2. */
export function letterPoints(letter: string, rulesVersion: number): number {
  return Math.min(LETTER_POINTS[letter.toUpperCase()] ?? 0, scoringFor(rulesVersion).maxLetterPoints)
}

/** A word's score on a board: letter points × (1 + gold bonuses). CRAMP on No. 412 is 11 × 8 = 88. */
export function scoreWord(word: string, board: Scoring): WordScore {
  const { maxLetterPoints, goldOncePerWord } = scoringFor(board.rulesVersion)
  const seen = new Set<string>()
  let pts = 0
  let mult = 1
  for (const l of word.toUpperCase()) {
    pts += Math.min(LETTER_POINTS[l] ?? 0, maxLetterPoints)
    if (goldOncePerWord && seen.has(l)) continue
    seen.add(l)
    mult += board.gold[l] ?? 0
  }
  return { pts, mult, total: pts * mult }
}
